import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, keyframes } from '@mui/material';

interface RunningAvatarProps {
  /** Zeit in SEKUNDEN bis das Easter Egg erscheint (Testwert!) */
  delaySeconds?: number;
  /** Intervall in SEKUNDEN zwischen den Läufen */
  intervalSeconds?: number;
  /** Aktivierbar/Deaktivierbar */
  enabled?: boolean;
}

// ============ SPRITE SHEET ANIMATIONEN ============

// Running Sprite (8 frames)
const spriteRunning = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: -1280px 0; }  /* 8 frames × 160px */
`;

// Jumping Sprite (6 frames)
const spriteJumping = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: -960px 0; }  /* 6 frames × 160px */
`;

// Flying Sprite (8 frames)
const spriteFlying = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: -1280px 0; }  /* 8 frames × 160px */
`;

// Waving Sprite (6 frames)
const spriteWaving = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: -960px 0; }  /* 6 frames × 160px */
`;

// Underwear Sprite (8 frames)
const spriteUnderwear = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: -1280px 0; }  /* 8 frames × 160px */
`;

// ============ BEWEGUNGS-ANIMATIONEN ============

// Von links nach rechts
const runLeftToRight = keyframes`
  0% { transform: translateX(-200px); }
  100% { transform: translateX(calc(100vw + 200px)); }
`;

// Von rechts nach links (gespiegelt)
const runRightToLeft = keyframes`
  0% { transform: translateX(calc(100vw + 200px)); }
  100% { transform: translateX(-200px); }
`;

// Hüpfend von links nach rechts
const hopLeftToRight = keyframes`
  0% { transform: translate(-200px, 0px); }
  25% { transform: translate(calc(25vw - 50px), -100px); }
  50% { transform: translate(calc(50vw), 0px); }
  75% { transform: translate(calc(75vw + 50px), -100px); }
  100% { transform: translate(calc(100vw + 200px), 0px); }
`;

// Hüpfend von rechts nach links
const hopRightToLeft = keyframes`
  0% { transform: translate(calc(100vw + 200px), 0px); }
  25% { transform: translate(calc(75vw + 50px), -100px); }
  50% { transform: translate(calc(50vw), 0px); }
  75% { transform: translate(calc(25vw - 50px), -100px); }
  100% { transform: translate(-200px, 0px); }
`;

// Fliegend (Flugbahn)
const flyLeftToRight = keyframes`
  0% { transform: translate(-200px, 30px); }
  25% { transform: translate(calc(25vw - 50px), -50px); }
  50% { transform: translate(calc(50vw), -80px); }
  75% { transform: translate(calc(75vw + 50px), -50px); }
  100% { transform: translate(calc(100vw + 200px), 30px); }
`;

// Fliegend von rechts nach links
const flyRightToLeft = keyframes`
  0% { transform: translate(calc(100vw + 200px), 30px); }
  25% { transform: translate(calc(75vw + 50px), -50px); }
  50% { transform: translate(calc(50vw), -80px); }
  75% { transform: translate(calc(25vw - 50px), -50px); }
  100% { transform: translate(-200px, 30px); }
`;

// ============ AVATAR-VARIANTEN (Sprite Sheets) ============
const avatars = [
  { 
    src: '/assets/avatar_running_sprite.webp',
    name: 'running',
    frames: 8,
    spriteAnim: spriteRunning,
    duration: 0.8  // 8 frames à 0.1s
  },
  { 
    src: '/assets/avatar_underwear_sprite.webp', 
    name: 'underwear',
    frames: 8,
    spriteAnim: spriteUnderwear,
    duration: 0.8
  },
  { 
    src: '/assets/avatar_waving_sprite.webp', 
    name: 'waving',
    frames: 6,
    spriteAnim: spriteWaving,
    duration: 0.6
  },
  { 
    src: '/assets/avatar_flying_sprite.webp', 
    name: 'flying',
    frames: 8,
    spriteAnim: spriteFlying,
    duration: 0.8
  },
  {
    src: '/assets/avatar_jumping_sprite.webp',
    name: 'jumping',
    frames: 6,
    spriteAnim: spriteJumping,
    duration: 0.6
  }
];

// ============ ANIMATIONSTYPEN ============
type AnimationType = 'runLR' | 'runRL' | 'hopLR' | 'hopRL' | 'flyLR' | 'flyRL' | 'wave';

interface AnimationConfig {
  keyframe: any;
  duration: number;
  direction: 'left' | 'right';
  position: 'left' | 'right';
}

const animations: Record<AnimationType, AnimationConfig> = {
  runLR: { keyframe: runLeftToRight, duration: 5, direction: 'left', position: 'left' },
  runRL: { keyframe: runRightToLeft, duration: 5, direction: 'right', position: 'right' },
  hopLR: { keyframe: hopLeftToRight, duration: 6, direction: 'left', position: 'left' },
  hopRL: { keyframe: hopRightToLeft, duration: 6, direction: 'right', position: 'right' },
  flyLR: { keyframe: flyLeftToRight, duration: 7, direction: 'left', position: 'left' },
  flyRL: { keyframe: flyRightToLeft, duration: 7, direction: 'right', position: 'right' },
  wave: { keyframe: runLeftToRight, duration: 5, direction: 'left', position: 'left' },
};

const RunningAvatar: React.FC<RunningAvatarProps> = ({ 
  delaySeconds = 3,      // 3 Sekunden zum Testen
  intervalSeconds = 6,   // 6 Sekunden zwischen den Aktionen
  enabled = true         // STANDARDMÄSSIG AKTIV!
}) => {
  const [isRunning, setIsRunning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentAvatar, setCurrentAvatar] = useState(avatars[0]);
  const [currentAnimation, setCurrentAnimation] = useState<AnimationType>('runLR');
  const initialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = () => {
    if (initialTimerRef.current) {
      clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
      runTimeoutRef.current = null;
    }
  };

  const triggerRun = useCallback(() => {
    if (!enabled) return;

    const randomAvatar = avatars[Math.floor(Math.random() * avatars.length)];
    setCurrentAvatar(randomAvatar);
    let animationTypes: AnimationType[];

    if (randomAvatar.name === 'flying') {
      animationTypes = ['flyLR', 'flyRL'] as AnimationType[];
    } else if (randomAvatar.name === 'jumping') {
      animationTypes = ['hopLR', 'hopRL'] as AnimationType[];
    } else {
      animationTypes = ['runLR', 'runRL', 'hopLR', 'hopRL'] as AnimationType[];
    }

    const randomAnimation = animationTypes[Math.floor(Math.random() * animationTypes.length)];
    setCurrentAnimation(randomAnimation);
    setIsRunning(true);

    const duration = animations[randomAnimation].duration * 1000;
    if (runTimeoutRef.current) {
      clearTimeout(runTimeoutRef.current);
    }
    runTimeoutRef.current = setTimeout(() => {
      setIsRunning(false);
      runTimeoutRef.current = null;
    }, duration);
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsRunning(false);
      setHasStarted(false);
      return;
    }

    const initialTimer = setTimeout(() => {
      setHasStarted(true);
      triggerRun();
    }, delaySeconds * 1000);
    initialTimerRef.current = initialTimer;

    return () => clearTimers();
  }, [delaySeconds, enabled, triggerRun]);

  useEffect(() => {
    if (!hasStarted || !enabled) return;

    // Intervall für wiederholte Läufe
    const interval = setInterval(() => {
      triggerRun();
    }, intervalSeconds * 1000);
    intervalRef.current = interval;

    return () => clearTimers();
  }, [hasStarted, intervalSeconds, enabled, triggerRun]);

  if (!isRunning || !enabled) return null;

  const anim = animations[currentAnimation];
  const isRightToLeft = anim.direction === 'right';

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '20px',
        left: 0,
        zIndex: 99999,
        pointerEvents: 'none',
        animation: `${anim.keyframe} ${anim.duration}s linear forwards`,
        willChange: 'transform',
        isolation: 'isolate',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* SPRITE SHEET Container */}
      <Box
        sx={{
          width: '160px',
          height: '200px',
          backgroundImage: `url(${currentAvatar.src})`,
          backgroundSize: `${currentAvatar.frames * 160}px 200px`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: '0 0',
          animation: `${currentAvatar.spriteAnim} ${currentAvatar.duration}s steps(${currentAvatar.frames}) infinite`,
          transform: isRightToLeft ? 'scaleX(-1)' : 'none',
          filter: 'drop-shadow(3px 3px 6px rgba(0,0,0,0.4))',
          imageRendering: 'crisp-edges',  // Scharfe Pixel
        }}
      />
    </Box>
  );
};

export default RunningAvatar;
